import React, { useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeUnits } from 'Core/hooks';
import { lastReadingSelectors } from 'Core/realtime/redux/lastReadingSlice';
import { NavLink, useLocation } from "react-router-dom";
import { useIntl } from 'react-intl'
import moment from 'moment';

// Material UI
import { Box, Grid, Icon, Typography } from '@mui/material';
// Components
import { Loading, Button } from 'Components';
import Tabs, { TabContainer, TabButton, TabIconButton } from 'Components/Tabs';
import VidFleetVideo from 'Modules/.common/components/VidFleetVideo';
import Video from 'Modules/.common/components/Video';
import SmartWitnessVideo from 'Modules/.common/components/SmartWitnessVideo';
import SmartWitnessSnapshot from 'Modules/.common/components/SmartWitnessSnapshot';

import CameraSignalStatus from 'Modules/.common/components/VidFleetVideo/.components/cameraSignalStatus';
import { TypeObjectMap } from '../../../../components/Map/providers/enums';

import {
  getFormattedDateByTimeZone,
  getPreferencesByKey,
  checkDeviceData
} from 'Core/data/Helpers';
import { getWarningCategory } from 'Core/hooks/utils';
import { translateEvent } from 'Modules/.common/utils/translateEvents';

// Views
import Location from './.components/location';
import VehicleInfo from './.components/vehicleInfo';
import Settings from './.components/settings';
import Snapshot from './.components/snapshot';
import RequestClip from './.components/requestClip';
import LandmarkInfo from './.components/landmarkInfo';
import SearchMapInfo from './.components/searchMapInfo';
import { TripEventInfoCard } from './.components/tripEventInfoCard';
import GeofenceInfo from './.components/geofenceInfo';
import RoutePointInfo from './.components/routePointInfo';
import RoutePointInfoLog from './.components/routePointInfoLog';
import DeviceInfo from './.components/deviceInfo'
import { getOemDataTarget } from './.components/oemDataTarget';
import { OEM_DATA_ICON, useOemDataAction } from 'Modules/.common/components/OemDataModal';
import ClipCard from "../../../video/views/main/.components/clips/.components/clipCard";

import { MapUtilitiesTypes } from 'Modules/realtimeMaps/main/components/MapUtilitiesMenu/enums';
import { HasPermission } from 'Core/security';

// Styles
import './infoBubble.scss';
import '../../../../components/Map/providers/google/InfoWindow/style.scss'

// Actions
import {
  getMarkerDetails,
  getVehicleTrails,
  selectModuleMapSettings,
  setVehicleTrailToPrint,
  searchUnitTypeEvents,
  openTroubleshootDeviceModal,
  openOverViewUnit,
  getPublicMarkerDetails,
  openWarningsModal
} from 'Redux/actions';
import { LiveMetricsVideo } from '../LightMetricsVideo';
import { RequestClipLightMetrics } from './.components/requestClipLightMetrics';
import RequestVideo from 'Modules/.common/components/RequestVideo';
import { stopLightMetricsLiveStreaming } from '../LightMetricsVideo/redux/slice';
import { MitacVideo } from '../MitacVideo';
import { setUnitVehicleTrail } from 'Modules/realtimeMaps/main/components/VehicleTrails/redux/slice';

const LIGTHMETRICS_MANUFACTURER = "LIGHTMETRICS";
const MITAC_MANUFACTURER = "MITAC";

/**
 * Simplified vehicle info for ETA link public view.
 * Reads from etaLinkRedux.response.unitData (already fetched by the saga)
 * since the InfoBubble pointer lacks deviceId for a separate API call.
 */
const EtaLinkVehicleInfo = ({ data, messages, position }) => {
  // Primary source: unitData from etaLink saga (getPublicMarkerDetail already fetched)
  const unitData = useSelector(state => state.etaLinkRedux?.response?.unitData);
  const unit = unitData?.markerDetail?.unit || data?.markerDetail?.unit;
  const lastReading = unit?.lastReading;
  const address = lastReading?.address;
  const formattedAddress = address
    ? (typeof address === 'object'
      ? (address.formatted || `${address.address || ''}${address.city ? `, ${address.city}` : ''}${address.state ? `, ${address.state}` : ''}${address.zip ? ` ${address.zip}` : ''}`)
      : address)
    : '';
  const coordinates = position
    ? `${position.lat?.toFixed?.(6) ?? position.lat}, ${position.lng?.toFixed?.(6) ?? position.lng}`
    : '';

  return (
    <div className='info-window-tab-streetview'>
      {(unit?.label || unit?.lastReading?.deviceLabel) && (
        <div style={{ padding: '16px 16px 0 16px' }}>
          <Typography noWrap style={{ fontWeight: 'bold' }}>
            {unit?.label || unit?.lastReading?.deviceLabel}
          </Typography>
        </div>
      )}
      <div style={{ borderTop: 'solid 1px #e4e4e4' }}></div>
      <div>
        <table className="info-bubble-table">
          <tbody>
            {unit?.driverName && unit.driverName !== 'true' && unit.driverName !== 'null' && (
              <tr>
                <td>{messages['infoBubble_driverName'] || 'Driver'}</td>
                <td><span>{unit.driverName}</span></td>
              </tr>
            )}
            {lastReading?.eventName && (
              <tr>
                <td>{messages['infoBubble_event'] || 'Event'}</td>
                <td><span>{translateEvent(lastReading.eventName, messages)}</span></td>
              </tr>
            )}
            {formattedAddress && (
              <tr>
                <td>{messages['infoBubble_address'] || 'Address'}</td>
                <td><span>{formattedAddress}</span></td>
              </tr>
            )}
            {coordinates && (
              <tr>
                <td>{messages['infoBubble_latLng'] || 'Lat/Lng'}</td>
                <td><span>{coordinates}</span></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * ETA link vehicle content: shows snapshot as primary view when available,
 * with unit label + ETA in the header. Falls back to unit info table.
 */
const EtaLinkVehicleContent = ({ data, messages, position, id, type, user, response, isEtaLink, hasSnapshot }) => {
  const {
    loadingSnapshot,
    snapshot: snapshotData,
    errorSnapshot,
  } = useSelector((state) => state.infoBubbleRedux);
  const etaTime = useSelector((state) => state.etaLinkRedux?.eta);
  const etaDistance = useSelector((state) => state.etaLinkRedux?.etaDistance);
  const unit = data?.markerDetail?.unit;

  const snapshotAvailable = hasSnapshot && !loadingSnapshot && !errorSnapshot && snapshotData?.url;
  const snapshotFailed = hasSnapshot && !loadingSnapshot && (errorSnapshot || !snapshotData?.url);

  return (
    <div>
      {/* Always mount Snapshot when hasSnapshot to trigger the API fetch */}
      {hasSnapshot && (
        <div style={{ display: snapshotAvailable ? 'block' : 'none' }}>
          <div style={{ padding: '10px 16px' }}>
            <Typography variant="subtitle1" style={{ fontWeight: 'bold', fontSize: 14, textTransform: 'uppercase', marginBottom: 4 }}>
              {unit?.label || ''}
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {etaTime && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Icon style={{ fontSize: 18, color: '#666' }}>schedule</Icon>
                  <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: 13 }}>
                    {etaTime}
                  </Typography>
                </div>
              )}
              {etaDistance && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Icon style={{ fontSize: 18, color: '#666' }}>straighten</Icon>
                  <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: 13 }}>
                    {etaDistance}
                  </Typography>
                </div>
              )}
            </div>
          </div>
          <Snapshot id={id} type={type} user={user} data={response} messages={messages} isEtaLink={isEtaLink} width={500} />
        </div>
      )}

      {/* Loading state */}
      {hasSnapshot && loadingSnapshot && <Loading full />}

      {/* Fallback: no snapshot capability or snapshot failed */}
      {(!hasSnapshot || snapshotFailed) && (
        <EtaLinkVehicleInfo data={data} messages={messages} position={position} />
      )}
    </div>
  );
};

/**
 * Simplified route point info for ETA link public view.
 * Uses data already available in the marker (no API call needed).
 */
const EtaLinkRoutePointInfo = ({ mapObject, messages }) => {
  const label = mapObject?.label || '';
  const lat = mapObject?.position?.lat;
  const lng = mapObject?.position?.lng;
  const coordinates = (lat != null && lng != null)
    ? `${typeof lat === 'number' ? lat.toFixed(6) : lat}, ${typeof lng === 'number' ? lng.toFixed(6) : lng}`
    : '';
  const sequence = mapObject?.value != null ? parseInt(mapObject.value) + 1 : '';

  return (
    <div className='info-window-tab-streetview'>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={{ padding: '16px 45px 16px 16px' }}
      >
        <Grid item xs zeroMinWidth>
          <Typography noWrap style={{ fontWeight: 'bold' }}>
            {label || `Stop ${sequence}`}
          </Typography>
        </Grid>
      </Grid>
      <div style={{ borderTop: 'solid 1px #e4e4e4' }}></div>
      <div>
        <table className="info-bubble-table">
          <tbody>
            {sequence && (
              <tr>
                <td>{messages['infoBubble_type'] || 'Type'}</td>
                <td><span>{`Stop ${sequence}`}</span></td>
              </tr>
            )}
            {coordinates && (
              <tr>
                <td>{messages['infoBubble_latLng'] || 'Lat/Lng'}</td>
                <td><span>{coordinates}</span></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const InfoBubble = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { messages, locale } = useIntl();

  const { position, type, id, deviceId, showStreetView, closeInfoBubble, mapReference, isEtaLink, isClipCard, clipRequestData } = props;

  const loading = useSelector(state => state.infoBubbleRedux.loadingGetMarkerDetails);
  const response = useSelector(state => state.infoBubbleRedux.getMarkerDetailsResponse);
  const getMarkerDetailsId = useSelector(state => state.infoBubbleRedux.getMarkerDetailsId);
  // Local trail row data dispatched from the sidebar at click time — used to
  // pre-populate the vehicleTrail branch of previewData so the Location tab
  // renders immediately without waiting for the getMarkerDetails API call.
  const trailPreviewData = useSelector(state => state.infoBubbleRedux.trailPreviewData);

  // For ETA link, unit data is already fetched by the etaLink saga — use it
  // as the effective response so hasVideo(), hasSnapshot, and Snapshot all
  // have access to the full marker detail without a duplicate API call.
  const etaLinkUnitData = useSelector(state =>
    isEtaLink ? state.etaLinkRedux?.response?.unitData : null
  );
  const effectiveResponse = isEtaLink && etaLinkUnitData ? etaLinkUnitData : response;

  // Read scheduleSnapshot directly from etaLink Redux instead of prop
  // drilling — the prop is frozen at click time and can be stale if the
  // etaLink response was being refreshed when the user opened the modal.
  // Cache the last truthy value via ref to avoid flickering every 15s
  // when the etaLink reducer briefly sets response to null during refresh.
  const scheduleSnapshotRaw = useSelector(state =>
    isEtaLink ? state.etaLinkRedux?.response?.scheduleSnapshot : false
  );
  const scheduleSnapshotRef = useRef(false);
  if (scheduleSnapshotRaw) scheduleSnapshotRef.current = true;
  const scheduleSnapshot = isEtaLink ? scheduleSnapshotRef.current : false;

  // loadingTimerRef declared here; the full loading-sync useEffect is below
  // (after displayData and isTripsModeActive are available).
  const loadingTimerRef = useRef(null);

  // Clear loading indicator when unmounting
  useEffect(() => {
    return () => {
      const loadingRef = mapReference?.current?.infoBubbleLoadingRef;
      const updateFn = mapReference?.current?.infoBubbleUpdateRef;
      if (loadingRef) {
        loadingRef.current = false;
        updateFn?.current?.();
      }
    };
  }, []);

  // Reposition the modal overlay as soon as InfoBubble receives a new
  // position prop.  useLayoutEffect fires synchronously after the React
  // commit but BEFORE the browser paints, so the user never sees the
  // overlay at the previous marker's coordinates.
  useLayoutEffect(() => {
    if (position?.lat != null && position?.lng != null) {
      mapReference?.current?.updateModalPosition({
        position: {
          lat: parseFloat(position.lat),
          lng: parseFloat(position.lng)
        }
      });
    }
  }, [position?.lat, position?.lng]);

  // Select only the specific field — avoids re-renders on unrelated lightMetricsRedux changes
  const streamRequestId = useSelector(state => state.lightMetricsRedux.streamRequestId);
  const unitVehicleTrails = useSelector(state => state.vehicleTrailsSliceRedux.unitVehicleTrails);
  const isTripsModeActive = useSelector(state => state.vehicleTrailsSliceRedux.isTripsModeActive);
  const readings = useSelector(state => lastReadingSelectors.selectEntities(state));

  const units = useRealtimeUnits({
    trackFields: [
      'id',
      'name',
      'label',
      'Label',
      'disabled',
      'driverId',
      'vehicleStatusId',
      'tags',
      'useEcuVin',
      'useEcuOdometer',
      'useEcuEngineHours',
      'useEcuSpeed',
      'unitType',
      'typeId',
      'timeZone',
      'manufacturerName',
    ],
  });

  const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);
  const user = useSelector(state => state.securityRedux.user);


  const editLandMark = useSelector((state) => state.realtimeMapLandmarkCreateEditModuleRedux.response);
  const preferencesUser = useSelector(state => state.preferencesRedux.preferencesUser);
  const keyPreferences = "mapSettings";
  const streeViewContainer = useRef();
  const clipRequestRef = useRef();
  const smartwitnessRef = useRef();
  const [streetViewVisible, setStreetViewVisible] = useState(false);
  const [tabButton, setTabButton] = useState({ show: false, title: null });
  const [errors, setErrors] = useState({});
  const [internalVideoTab, setInternalVideoTab] = useState(0);
  const [disableBtn, setDisableBtn] = useState(false)
  const [tooltip, setTooltip] = useState('')
  const [changeIdUnit, setChangeIdUnit] = useState(0);
  const [preferences, setPreferences] = useState({});
  const [dataClipRequest, setDataClipRequest] = useState(null);

  const lastReading = response?.markerDetail?.unit?.lastReading;

  // Build a lightweight preview response from the mapObject props that are
  const catalogLandmarks = useSelector(state => state.catalogsRealtimeRedux.landmarks);
  const catalogGeofences = useSelector(state => state.catalogsRealtimeRedux.geofences);

  // already available at click time.  This lets the Location tab render
  // immediately (unit label, event name, coordinates, heading, driver)
  // while the full getMarkerDetails API call completes in the background.
  const previewData = useMemo(() => {
    if (type === 'Vehicle' || type === 'Trail') {
      // For Trail type, if local row data is available (dispatched from the
      // sidebar via setTrailPreviewData), build a full vehicleTrail branch so
      // the Location tab shows address, speed, event, time and lat/lng
      // immediately while the API call completes in the background.
      // Guard: only use trailPreviewData when it belongs to the current marker.
      // Without this check a stale preview from the previously clicked card
      // would briefly appear while the new API call is in-flight.
      const vtPreview = (type === 'Trail' && trailPreviewData && String(trailPreviewData.id) === String(id))
        ? {
            id: trailPreviewData.id,
            eventName: trailPreviewData.eventName,
            address: trailPreviewData.address,
            formattedAddress: trailPreviewData.address,
            speed: trailPreviewData.speed,
            ecuSpeed: 0,
            speedLimit: trailPreviewData.speedLimit,
            unitTime: trailPreviewData.unitTime,
            latitude: trailPreviewData.latitude,
            longitude: trailPreviewData.longitude,
            driverName: trailPreviewData.driverName || '',
            driverId: trailPreviewData.driverId || (trailPreviewData.driverName ? 1 : 0),
            unitType: trailPreviewData.unitType,
            heading: trailPreviewData.heading || props.heading || 0,
          }
        : null;

      return {
        id,
        markerDetail: {
          ...(vtPreview && { vehicleTrail: vtPreview }),
          unit: {
            id,
            // For Trail type the mapObject label is the waypoint number, not the
            // vehicle name — use unitVehicleTrails (already in Redux scope) instead.
            label: type === 'Trail'
              ? (unitVehicleTrails?.label || unitVehicleTrails?.Label || props.label || '')
              : (props.label || ''),
            driverName: props.labelDriver || '',
            driverId: props.labelDriver ? 1 : 0,
            useEcuSpeed: false,
            timeZone: { name: trailPreviewData?.vehicleTimezone || '' },
            lastReading: {
              latitude: position?.lat,
              longitude: position?.lng,
              heading: props.heading || 0,
              eventName: props.eventName || '',
              unitType: trailPreviewData?.unitType || '',
            },
            crews: [],
            tags: type === 'Trail' ? (unitVehicleTrails?.tags || []) : [],
          },
        },
        _isPreview: true,
      };
    }
    if (type === 'Landmark') {
      const idNorm = id?.toString()?.replace('landmark_', '');
      const cached = catalogLandmarks?.find(l => String(l.id) === String(idNorm));
      return {
        id,
        markerDetail: {
          landmark: {
            id: idNorm,
            name: cached?.name || props.label || '',
            address: cached?.address || '',
            shape: cached?.shape || '',
            note: cached?.note || '',
            latitude: cached?.latitude ?? position?.lat,
            longitude: cached?.longitude ?? position?.lng,
            tags: cached?.tags || [],
          },
        },
        _isPreview: true,
      };
    }
    if (type === 'Geofence') {
      const idNorm = id?.toString()?.replace('geofence_', '');
      const cached = catalogGeofences?.find(g => String(g.id) === String(idNorm));
      return {
        id,
        markerDetail: {
          geofence: {
            id: idNorm,
            name: cached?.name || props.label || '',
            radius: cached?.radius || 0,
            tags: cached?.tags || [],
            center_latlong: cached?.lat && cached?.lng ? `${cached.lat},${cached.lng}` : null,
            coordinates: cached?.coordinates?.length
              ? cached.coordinates
              : [{ latitude: position?.lat, longitude: position?.lng }],
          },
        },
        _isPreview: true,
      };
    }
    return null;
  }, [id, type, trailPreviewData, unitVehicleTrails, catalogLandmarks, catalogGeofences]);

  // Use real response when available; fall back to preview for instant display.
  // Double-validate ownership: (a) the reducer's tracked request ID must match
  // this marker, AND (b) the response object's own ID must match.  This guards
  // against both stale Redux state and late-arriving saga responses.
  const _idNormalized = id?.toString()?.split('_')?.[1] || id?.toString();
  const _responseOwned = response
    && String(getMarkerDetailsId) === String(_idNormalized)
    && (!response.id || String(response.id) === String(_idNormalized));
  const displayData = _responseOwned ? response : previewData;

  const displayDataWithRealtimeUnit = useMemo(() => {
    if (!displayData?.markerDetail?.unit) return displayData;
    const unitId = displayData.markerDetail.unit.id;
    const rtUnit = units?.find((u) => String(u.id) === String(unitId));
    const rtReading = readings?.[unitId];
    if (!rtUnit && !rtReading) return displayData;
    return {
      ...displayData,
      markerDetail: {
        ...displayData.markerDetail,
        unit: {
          ...displayData.markerDetail.unit,
          useEcuVin: rtUnit?.useEcuVin ?? displayData.markerDetail.unit.useEcuVin,
          useEcuOdometer: rtUnit?.useEcuOdometer ?? displayData.markerDetail.unit.useEcuOdometer,
          useEcuEngineHours: rtUnit?.useEcuEngineHours ?? displayData.markerDetail.unit.useEcuEngineHours,
          timeZone: rtUnit?.timeZone ?? displayData.markerDetail.unit.timeZone,
          manufacturerName: rtUnit?.manufacturerName ?? displayData.markerDetail.unit.manufacturerName,
          lastReading: rtReading
            ? { ...displayData.markerDetail.unit.lastReading, ...rtReading }
            : displayData.markerDetail.unit.lastReading,
        },
      }};
  }, [displayData, readings, units]);

  // OEM data action for the header toolbar (next to Troubleshooting): the
  // Vehicle bubble opens the unit's latest reading, the Trail bubble that point.
  const oemUnit = displayDataWithRealtimeUnit?.markerDetail?.unit;
  const oemAction = useOemDataAction({
    ...getOemDataTarget(type, displayDataWithRealtimeUnit, deviceId),
    manufacturerName: oemUnit?.manufacturerName,
    unitLabel: oemUnit?.label,
  });
  const showOemAction = (type === 'Vehicle' || type === 'Trail') && !isEtaLink && oemAction.visible
    && !(displayDataWithRealtimeUnit?._isPreview && loading);

  // Sync loading state to InfoWindow via shared refs (minimum display: 600ms).
  // Only show the top LinearProgress bar when re-fetching data for an already-
  // visible modal (displayData exists).  During the initial load the InfoBubble
  // content itself renders a full <Loading /> spinner, so adding the top bar
  // would create a redundant "double loading" indicator.
  // In trips mode the LinearProgress line is the sole loading indicator
  // (no full spinner), keeping the UX uniform across window sizes.
  useEffect(() => {
    const loadingRef = mapReference?.current?.infoBubbleLoadingRef;
    const updateFn = mapReference?.current?.infoBubbleUpdateRef;
    if (!loadingRef) return;

    if (loading && (displayData || isTripsModeActive)) {
      // Show immediately — content or trips placeholder is behind the bar
      clearTimeout(loadingTimerRef.current);
      loadingRef.current = true;
      updateFn?.current?.();
    } else {
      // Hide (with a small delay so the bar doesn't flash)
      loadingTimerRef.current = setTimeout(() => {
        loadingRef.current = false;
        updateFn?.current?.();
      }, 600);
    }
    return () => clearTimeout(loadingTimerRef.current);
  }, [loading, displayData, isTripsModeActive]);

  let currentTab = 0
  if (type == "Trail") {
    currentTab = parseInt(localStorage.getItem("info-bubble-last-tab") || 0);
  }

  const [mainTab, setMainTab] = useState(currentTab);
  const [isSmartWitnessVideoAvailable, setIsSmartWitnessVideoAvailable] = useState(false);

  useEffect(() => {
    // Reset internal state when switching markers.
    // Without a key prop, React reuses this component instance — fast prop
    // diff (~5ms) instead of expensive unmount/remount (50-200ms).  We
    // manually reset state that should not carry over between markers.
    setStreetViewVisible(false);
    setTabButton({ show: false, title: null });
    setErrors({ markerDetails: null });
    setInternalVideoTab(0);
    setDisableBtn(false);
    setTooltip('');
    setIsSmartWitnessVideoAvailable(false);
    setDataClipRequest(null);
    if (type === "Trail") {
      setMainTab(parseInt(localStorage.getItem("info-bubble-last-tab") || 0));
    } else {
      setMainTab(0);
    }

    if (type && id) {
      let idToSearch = id ? id.toString() : ''
      idToSearch = idToSearch.split('_')[1] || idToSearch
      if (!isEtaLink && !isClipCard) {
        dispatch(getMarkerDetails({ type, id: idToSearch, unitId: deviceId }));
      } else if(isEtaLink) {
        // ETA link Vehicle data is already fetched by the etaLink saga
        // (stored in etaLinkRedux.response.unitData) — no need for a
        // second getPublicMarkerDetail call which causes flicker.
        // RoutePoint info is already in the marker props.
      } else if(isClipCard) {
        setDataClipRequest(clipRequestData)
      }
    }
  }, [id, type]);

  useEffect(() => {
    if ((editLandMark && !editLandMark?.error) && type && id && !isEtaLink) {
      let idToSearch = id ? id.toString() : ''
      idToSearch = idToSearch.split('_')[1] || idToSearch
      dispatch(getMarkerDetails({ type, id: idToSearch, unitId: deviceId }));
    }
  }, [editLandMark])

  useEffect(() => {
    if (streetViewVisible) {
      setErrors({ streetView: null });
      showStreetView(streeViewContainer.current, position, (googleStates, status) => {
        if (googleStates.ZERO_RESULTS === status || googleStates.UNKNOWN_ERROR === status) {
          let e = { streetView: messages['infoBubble_streetViewNotAvailable'] };
          setErrors(e);
        }
      });
    }
  }, [streetViewVisible]);

  useEffect(() => {
    if (response) {
      if (response.error) {
        setErrors({ markerDetails: messages['infoBubble_getMarkerDetailsError'] });
      } else {
        setErrors({ markerDetails: null });
      }

      //update data from the unit — only when the response belongs to the
      // current marker.  On mount, Redux may still hold the PREVIOUS
      // marker's response (the new getMarkerDetails hasn't resolved yet).
      // Without this guard, updateUnitForData would reposition the overlay
      // to the old marker's lat/lng, causing a visible jump before the
      // new API response arrives and corrects it.
      if (type === "Vehicle" && _responseOwned) {
        updateUnitForData(response)
      }
    }
  }, [response]);

  useEffect(() => {
    if (preferencesUser) {
      let newPreferences = getPreferencesByKey(preferencesUser, keyPreferences);
      setPreferences(newPreferences);
    }
  }, [preferencesUser]);

  const handleClipRequestError = () => {
    setDisableBtn(true)
    setTooltip(response?.markerDetail?.linkedDevice?.manufacturerName?.toUpperCase() === LIGTHMETRICS_MANUFACTURER ? messages['videoMainView_errorRequestClipLM'] : messages['infoBubble_clipRequestError'])
  }

  useEffect(() => {
    if (!loading) {
      setChangeIdUnit(getMarkerDetailsId)
    }
  }, [loading])

  const updateUnitForData = (data) => {

    if (!data?.markerDetail?.unit?.lastReading?.latitude || !data?.markerDetail?.unit?.lastReading?.longitude) {
      return
    }

    //if not a natural number or float number
    if (isNaN(parseFloat(data?.markerDetail?.unit?.lastReading?.latitude)) ||
      !isFinite(data?.markerDetail?.unit?.lastReading?.latitude) ||
      isNaN(parseFloat(data?.markerDetail?.unit?.lastReading?.longitude)) ||
      !isFinite(data?.markerDetail?.unit?.lastReading?.longitude)
    ) {
      return;
    }

    //permision for animation trail on maps
    const animationTrailPermission = !!(HasPermission(user, 'animationmap', [281]) && preferences?.animationmap)
    mapReference.current?.updateObject({
      id: data.id,
      position: { lat: parseFloat(data?.markerDetail?.unit?.lastReading?.latitude), lng: parseFloat(data?.markerDetail?.unit?.lastReading?.longitude) },
      label: data?.markerDetail?.unit?.label,
      labelDriver: data?.markerDetail?.unit?.driverName,
      heading: data?.markerDetail?.unit?.lastReading?.heading || 0,
      color: data?.color || '',
      eventName: data?.markerDetail?.unit?.lastReading?.eventName,
      // Mitac has no linkedUnit (built-in camera); detect it via unitVersionProduct instead
      hasVideo: data?.markerDetail?.unit?.linkedUnitId > 0
        || data?.markerDetail?.unit?.unitVersionProduct?.toUpperCase() === MITAC_MANUFACTURER
        || false,
      trail: animationTrailPermission ? data?.markerDetail?.unit?.lastReading?.trail || [] : [], //eval the permision to trail.
      unitTime: parseInt(data?.markerDetail?.unit?.lastReading?.unitTime), //item.unitTime,
      rowId: data?.rowId || 0
    }, TypeObjectMap.Marker);

    if (data?.markerDetail?.unit?.isOffline) {
      //update the icon for offline devices
      mapReference?.current?.updateMarkerOfflineDevice(data?.id, data?.markerDetail?.unit?.isOffline)
    }

    //this validation preveng the movie modal when trail animation is active
    if (!animationTrailPermission || (animationTrailPermission && !data?.markerDetail?.unit?.lastReading?.trail?.length)) {
      mapReference.current?.updateModalPosition({
        position: {
          lat: parseFloat(data?.markerDetail?.unit?.lastReading?.latitude),
          lng: parseFloat(data?.markerDetail?.unit?.lastReading?.longitude)
        }
      })
      //Enable the drag event when the mouse is over the modal
      mapReference?.current?.mapItem?.setOptions({draggable: true})
    }

  }

  const hasVideo = useCallback(() => {
    if (effectiveResponse?.markerDetail?.linkedDevice && effectiveResponse?.markerDetail?.linkedDevice?.hasVideo) {
      return true
    }
    // LightMetrics units have built-in cameras (no separate linkedDevice)
    if (isEtaLink && effectiveResponse?.markerDetail?.unit?.manufacturerName?.toUpperCase() === LIGTHMETRICS_MANUFACTURER) {
      return true
    }
    // Mitac units also have a built-in camera and don't get a separate linkedDevice row;
    // the GraphQL query never returns unit.manufacturerName, only unit.unitVersionProduct === "mitac"
    if (effectiveResponse?.markerDetail?.unit?.unitVersionProduct?.toUpperCase() === MITAC_MANUFACTURER) {
      return true
    }
    return false;
  }, [effectiveResponse?.markerDetail?.linkedDevice?.hasVideo, isEtaLink, effectiveResponse?.markerDetail?.unit?.manufacturerName, effectiveResponse?.markerDetail?.unit?.unitVersionProduct]);

  const getVideo = useMemo(() => {
    // For ETA link, linkedDevice may be null; detect manufacturer from unit data.
    // Mitac is unit-embedded (no linkedDevice, no unit.manufacturerName) even outside ETA
    // link, so it always falls back to unit.unitVersionProduct.
    const unitManufacturer = effectiveResponse?.markerDetail?.unit?.manufacturerName?.toUpperCase();
    const unitVersionProduct = effectiveResponse?.markerDetail?.unit?.unitVersionProduct?.toUpperCase();
    const resolvedManufacturer = effectiveResponse?.markerDetail?.linkedDevice?.manufacturerName?.toUpperCase()
      || (isEtaLink ? unitManufacturer : null)
      || (unitVersionProduct === MITAC_MANUFACTURER ? MITAC_MANUFACTURER : null);

    switch (resolvedManufacturer) {
      case "VIDFLEET":
        const vidTabs = [];
        const sdCard = response?.markerDetail?.unit?.lastReading?.sdCard ? checkDeviceData(response?.markerDetail?.unit?.lastReading?.sdCard) : null
        if (response?.markerDetail?.vehicleTrail?.deviceData) {
          let clipID;
          let duration;
          let thumbnailUrl;
          try {
            let deviceDataObject = checkDeviceData(response?.markerDetail?.vehicleTrail?.deviceData);
            //const deviceDataObject = JSON.parse(JSON.parse(response?.markerDetail?.vehicleTrail?.deviceData));
            clipID = deviceDataObject.clipID;
            duration = deviceDataObject.duration;
            thumbnailUrl = deviceDataObject.thumbnailUrl;
          } catch (e) {
            console.log("Error while parse device data json vidfleet");
          }

          if (clipID && duration && !isEtaLink) {
            let eventTime = getFormattedDateByTimeZone(response?.markerDetail?.vehicleTrail?.unitTime, response?.markerDetail?.unit?.timeZone?.name, messages, "YYYY-MM-DD-HH-mm-ss");
            vidTabs.push({
              label: messages['infoBubble_clip'],
              render: () => {
                return (
                  <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="flex-start"
                    style={{ backgroundColor: 'black' }}
                  >
                    <Grid item>
                      <div className='info-window-size-width info-window-tab-vidFleet'>
                        <VidFleetVideo
                          name={`${clipID}-${response?.markerDetail?.unit?.id}-${eventTime}`}
                          messages={messages}
                          mode={response?.markerDetail?.linkedDevice?.manufacturerName == "SmartWitness" ? 'picture' : 'video'}
                          type="playback"
                          id={clipID}
                          autoplay={false}
                          deviceModel={(response?.markerDetail?.linkedDevice?.deviceModelName === "360" ? "360" : "4k")}
                          thumbnailUrl={thumbnailUrl}
                          pictureId={null}
                          disabled={false}
                        />
                      </div>
                    </Grid>
                  </Grid>
                );
              }
            });
          } else {
            vidTabs.push({
              label: messages['infoBubble_snapshot'],
              render: () => {
                return (
                  <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="flex-start"
                  >
                    <Grid item xs>
                      <Snapshot id={id} type={type} user={user} data={response} messages={messages} isEtaLink={isEtaLink} />
                    </Grid>
                  </Grid>
                );
              }
            });
          }
        } else {
          vidTabs.push({
            label: messages['infoBubble_snapshot'],
            render: () => {
              return (
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="flex-start"
                >
                  <Grid item xs>
                    <Snapshot id={id} type={type} user={user} data={response} messages={messages} isEtaLink={isEtaLink} />
                  </Grid>
                </Grid>
              );
            }
          });
        }

        if (type != "Trail" && !isEtaLink && HasPermission(user, "", [235])) {
          vidTabs.push({
            label: messages['infoBubble_live'],
            render: () => {
              return (
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="flex-start"
                >
                  <Grid item>
                    <div className='info-window-size-width info-window-tab-vidFleet'>
                      <VidFleetVideo
                        messages={messages}
                        mode="video"
                        type={response?.markerDetail?.linkedDevice?.liveMode ? response?.markerDetail?.linkedDevice?.liveMode?.toLowerCase() : "streaming"}
                        id={response?.markerDetail?.linkedDevice?.esn}
                        unitId={response?.markerDetail?.linkedDevice?.id}
                        autoplay={false}
                        deviceModel={(response?.markerDetail?.linkedDevice?.deviceModelName === "360" ? "360" : "4k")}
                        simProviderName={response?.markerDetail?.linkedDevice?.simProviderName}
                        vehicleEvent={response?.markerDetail?.unit?.lastReading?.eventName}
                        timeZone={response?.markerDetail?.unit?.timeZone?.name}
                      />
                    </div>
                  </Grid>
                </Grid>
              );
            }
          });
        }

        if (!isEtaLink) {
          vidTabs.push({
            label: messages['infoBubble_requestVideo'],
            render: () => {
              return (
                <RequestClip
                  ref={clipRequestRef}
                  id={response?.markerDetail?.linkedDevice?.id}
                  data={response}
                  type={type}
                  user={user}
                  messages={messages}
                  setDisableBtn={setDisableBtn}
                  setTooltip={setTooltip}
                />
              );
            }
          });
        }


        return (
          <Tabs
            tab={0}
            type="pill"
            border
            tabsClassName={`videoTitle tabs-pill ${isEtaLink && 'videoTitleEtaLink'}`}
            title={
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid item style={{ marginBottom: isEtaLink && 10 }}>
                  <span style={{ color: 'black', fontSize: 16, fontWeight: 'bold', padding: 16 }}>
                    {isEtaLink ? `${props.timeFormatToShow || ''} - ${props.distance} ${props.speedFormat}` : messages['infoBubble_video']}
                  </span>
                </Grid>
                <Grid item>
                  {
                    type != "Trail" && !isEtaLink &&
                    <CameraSignalStatus
                      id={response?.markerDetail?.linkedDevice?.esn}
                      unitId={response?.markerDetail?.linkedDevice?.id}
                      direction="row"
                      justifyContent="flex-end"
                      alignItems="center"
                      simProviderName={response?.markerDetail?.linkedDevice?.simProviderName}
                      vehicleEvent={response?.markerDetail?.unit?.lastReading?.eventName}
                      sdCard={sdCard}
                    />
                  }
                </Grid>
              </Grid>
            }
            onChange={(tab) => {
              //if (tab === 2 && type != "Trail") {
              if (tab === 2 || (tab === 1 && type === "Trail")) {
                setTabButton({ title: messages['infoBubble_openRequestClip'] });
              }
              setInternalVideoTab(tab);
            }}
          >
            {
              vidTabs.map((vidTab, index) => {
                return (
                  <TabContainer
                    key={index}
                    label={vidTab.label}
                    index={index}
                    destroyAfterChange={vidTab.label == messages['infoBubble_live'] ? true : false}
                    style={{ padding: 0 }}
                  >
                    {vidTab.render()}
                  </TabContainer>
                );
              })
            }
          </Tabs>
        );

      case "SMARTWITNESS":
        return (
          <Tabs
            tab={response?.markerDetail?.vehicleTrail?.deviceData?.includes("/SmartWitness/") ? 1 : 0}
            type="pill"
            border
            tabsClassName={"videoTitle tabs-pill"}
            title={messages["infoBubble_video"]}
            onChange={(tab) => {
              setInternalVideoTab(tab);
            }}
          >
            <TabContainer
              key={0}
              label={messages["infoBubble_live"]}
              index={0}
              style={{ padding: 0 }}
            >
              <div className='info-window-tab-video'>
                <SmartWitnessVideo
                  ref={smartwitnessRef}
                  recorderId={response?.markerDetail?.linkedDevice?.esn}
                  deviceId={response?.markerDetail?.unit?.id}
                  cameraChannel={
                    response?.markerDetail?.linkedDevice?.cameraChannel || "*"
                  }
                  messages={messages}
                  timeZone={response?.markerDetail?.unit?.timeZone?.name}
                  vehicleTrail={response?.markerDetail?.vehicleTrail}
                  setIsVideoAvailable={setIsSmartWitnessVideoAvailable}
                />
              </div>
            </TabContainer>
            {
              response?.markerDetail?.vehicleTrail?.deviceData?.includes("/SmartWitness/") &&
              <TabContainer
                key={1}
                label={messages["infoBubble_live_snapshots"]}
                index={1}
                style={{ padding: 0 }}
              >
                <div className='info-window-tab-video' style={{ width: "95%", marginLeft: "2%" }}>
                  <SmartWitnessSnapshot
                    messages={messages}
                    vehicleTrail={response?.markerDetail?.vehicleTrail}
                    height={250}
                    indicators={false}
                  />
                </div>
              </TabContainer>
            }
          </Tabs>
        );

      case "LIGHTMETRICS":
        if (type == "Trail" && response?.markerDetail?.vehicleTrail?.deviceId && response?.markerDetail?.vehicleTrail?.id) {
          let deviceDataObject = checkDeviceData(response?.markerDetail?.vehicleTrail?.deviceData);
          let hasMedia = deviceDataObject?.hasMedia && response?.markerDetail?.vehicleTrail?.eventName != "AutoSnapshot";

          return (
            <Tabs
              tab={0}
              type="pill"
              border
              tabsClassName={"videoTitle tabs-pill"}
              title={messages["infoBubble_video"]}
              onChange={(tab) => {
                setInternalVideoTab(tab);
              }}
            >
              <TabContainer
                key={0}
                label={hasMedia ? messages['infoBubble_clip'] : messages["infoBubble_snapshot"]}
                index={0}
                style={{ padding: 0 }}
              >
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="flex-start"
                  style={{ backgroundColor: 'black' }}
                >
                  {
                    !hasMedia ?
                      <Grid item>
                        <div className='info-window-size-width info-window-tab-vidFleet'>
                          <Video
                            readingId={response?.markerDetail?.vehicleTrail?.id}
                            deviceId={response?.markerDetail?.vehicleTrail?.deviceId}
                            unitTime={response?.markerDetail?.vehicleTrail?.unitTime}
                            messages={messages}
                            mode="snapshot"
                            deviceModel={(response?.markerDetail?.linkedDevice?.deviceModelName === "360" ? "360" : "4K")}
                            manufacturerName={"LightMetrics"}
                          />
                        </div>
                      </Grid> :
                      <Grid item>
                        <div className='info-window-size-width info-window-tab-vidFleet'>
                          <Video
                            readingId={response?.markerDetail?.vehicleTrail?.id}
                            deviceId={response?.markerDetail?.vehicleTrail?.deviceId}
                            messages={messages}
                            mode="playback"
                            autoplay={true}
                            deviceModel={(response?.markerDetail?.linkedDevice?.deviceModelName === "360" ? "360" : "4K")}
                            manufacturerName={"LightMetrics"}
                          />
                        </div>
                      </Grid>
                  }
                </Grid>
              </TabContainer>
            </Tabs>
          );
        }
        
        const lightMetricsTabs = [
          {
            label: messages['infoBubble_snapshot'],
            render: () => {
              return (
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="flex-start"
                >
                  <Grid item xs>
                    <Snapshot id={id} type={type} user={user} data={response} messages={messages} isEtaLink={isEtaLink} width={500} />
                  </Grid>
                </Grid>
              );
            }
          },
          // Live and RequestClip are authenticated features — skip for ETA link
          ...(!isEtaLink ? [
            {
              label: messages['infoBubble_live'],
              render: () => {
                return <Box width="535px" height="295px"><LiveMetricsVideo lastReading={lastReading} deviceId={response?.markerDetail?.unit?.linkedUnitId} /></Box>;
              }
            },
            {
              label: messages['infoBubble_requestVideo'],
              render: () => {
                // LightMetrics requests clips with the standalone RequestVideo component (Mitac below keeps the legacy form)
                return (
                  // Same 535x295 area as the other video tabs (the bubble has a fixed height and a bottom icon bar); the component scrolls inside it
                  <Box width="535px" height="295px" sx={{ overflowY: 'auto', boxSizing: 'border-box', padding: '0 16px 12px' }}>
                    <RequestVideo
                      initialCameraDeviceId={String(response?.markerDetail?.unit?.linkedUnitId)}
                      hideUnitSelector
                    />
                  </Box>
                );
              }
            }
          ] : [])
        ]

        return (
          <Tabs
              tab={0}
              type="pill"
              border
              tabsClassName={`videoTitle tabs-pill`}
              onChange={(tab) => {
                const stopLightMetricsLive = () => {
                  if (streamRequestId) {
                    dispatch(stopLightMetricsLiveStreaming({ deviceId: response?.markerDetail?.unit?.id, streamRequestId }))
                  }
                }
                stopLightMetricsLive()
              }}
          >
              {lightMetricsTabs.map((tab, index) => (
                <TabContainer
                  key={index}
                  label={tab.label}
                  index={index}
                  style={{ padding: 0 }}
                >
                  {tab.render()}
                </TabContainer>
              ))}
          </Tabs>
        );

      case "MITAC":
        const mitacTabs = [
          {
            label: messages['infoBubble_live'],
            render: () => {
              // Mitac has no linkedDevice; the unit's own esn is the USN sent to startMitacLiveStreaming
              return <Box width="535px" height="295px"><MitacVideo deviceId={response?.markerDetail?.unit?.esn} /></Box>;
            }
          },
          {
            label: messages['infoBubble_requestVideo'],
            render: () => {
              // Reuses the LightMetrics clip request form (same createClipRequest mutation,
              // just tagged with type="mitac") — Mitac clips are already treated like
              // LightMetrics clips elsewhere (clipCard, eventCard, etc.)
              return <Box display='flex' justifyContent='center' width="535px" height="295px"><RequestClipLightMetrics deviceId={response?.markerDetail?.unit?.id} type="mitac" onError={handleClipRequestError} /></Box>;
            }
          }
        ]

        return (
          <Tabs
            tab={0}
            type="pill"
            border
            tabsClassName={`videoTitle tabs-pill`}
            title={
              <span style={{ color: 'black', fontSize: 16, fontWeight: 'bold', padding: 16 }}>
                {messages['infoBubble_video']}
              </span>
            }
          >
            {mitacTabs.map((tab, index) => (
              <TabContainer
                key={index}
                label={tab.label}
                index={index}
                style={{ padding: 0 }}
              >
                {tab.render()}
              </TabContainer>
            ))}
          </Tabs>
        );

      default:
        return <Box width="535px" height="295px"><LiveMetricsVideo /></Box>;
    }
  }, [response?.markerDetail?.linkedDevice?.manufacturerName, response?.markerDetail?.vehicleTrail, isEtaLink, response?.markerDetail?.unit?.manufacturerName, response?.markerDetail?.unit?.unitVersionProduct]);

  if (type === "MapSearch") {
    return (
      <div className='info-window-size-width info-window-size-height' >
        <div>
          <Tabs
            tab={0}
            tabsPosition="top"
            type="icon"
            border
          >
            <TabContainer iconButton="room" label={messages['infoBubble_pointInfo']} index={0} style={{ padding: 0 }}>
              <SearchMapInfo data={props} messages={messages} />
            </TabContainer>
          </Tabs>
        </div>
      </div>
    )
  }

  const getMaintenanceWarnings = () => {
    //Getting information from the SD Card
    const sdCard = response?.markerDetail?.unit?.lastReading?.sdCard ? checkDeviceData(response?.markerDetail?.unit?.lastReading?.sdCard) : null
    const sdCardStatus = sdCard?.status?.toUpperCase();
    const isSdCardError = sdCardStatus && sdCardStatus !== "NORMAL";
    //Getting warnings
    const warnings = response?.markerDetail?.unit?.maintenanceWarningEventsCount || 0;
    //Check if the device is offline
    const isOffline = response?.markerDetail?.unit?.isOffline

    const { title, color } = getWarningCategory(isSdCardError, warnings, isOffline, messages)

    if (!title) return null

    return (
      <TabIconButton
        size="small"
        icon={"warning"}
        onClick={() => {
          if (HasPermission(user, "wgrs", [278])) {
            dispatch(openWarningsModal({ ...response.markerDetail.unit, sdCard }))
          }
        }
        }
        tooltip={title}
        style={{ color: color }}
      />
    );
  }

  const newIds = (getMarkerDetailsId != changeIdUnit)

  if (isTripsModeActive && type !== 'Landmark' && type !== 'Geofence') {
    if (loading && !isEtaLink) {
      // Placeholder keeps the window sized while the LinearProgress line
      // in the InfoWindow header indicates loading — no full spinner.
      return (
        <div style={{ width: 360, minHeight: 120 }} />
      );
    }
    if (response?.error || errors?.markerDetails) {
      return (
        <div style={{ width: 360, padding: 16 }}>
          <Typography variant="body2" color="error">
            {messages['infoBubble_getMarkerDetailsError']}
          </Typography>
        </div>
      );
    }
    if (response && !response.error && response?.markerDetail?.vehicleTrail) {
      return (
        <div style={{ padding: 12 }}>
          <TripEventInfoCard
            data={response}
            messages={messages}
            manufacturerName={response?.markerDetail?.linkedDevice?.manufacturerName}
            closeInfoBubble={closeInfoBubble}
          />
        </div>
      );
    }
    return (
      <div style={{ width: 360, minHeight: 60, padding: 12 }}>
        <Typography variant="body2" color="text.secondary">—</Typography>
      </div>
    );
  }

  if(isClipCard && dataClipRequest){
    return (
      <div className={`info-window-size-width info-window-size-height-clipRequest`}>
        <div style={{ padding: '25px 10px'}}>
          <ClipCard
                  row={dataClipRequest}
                  messages={messages}
                  unit={unitVehicleTrails}
                  hiddenDeleteButton={true}
                  isMapModal={true}
                  callback={() => {
                    /* tableViewRef?.current?.setNewPage(1);
                    handleChangePage(1);
                    setSelectedRows([]);
                    dispatch(
                      searchClipRequests({ ...allFilters, nextToken: null })
                    ); */
                  }}
                />
        </div>
      </div>
    )
  }

  return (
    <div className={`info-window-size-width ${!isEtaLink ? 'info-window-size-height' : 'info-window-size-height-eta'}`}>
      {
        ((!displayData && !isEtaLink) || (isEtaLink && loading && type !== 'RoutePoint'))
          ?
          <Loading full />
          :
          <div>
            {
              ((displayData && !displayData.error && !errors.markerDetails) || (isEtaLink && type === 'RoutePoint')) &&
              <Tabs
                tab={mainTab}
                tabsPosition="top"
                type="icon"
                border
                onChange={(tab) => {
                  setMainTab(tab);
                  localStorage.setItem("info-bubble-last-tab", tab);
                }}
                id="info-bubble-tab-main"
                tabsClassName={isEtaLink ? 'videoTitleEtaLink' : undefined}
              >
                {
                  type === "Landmark" && !isEtaLink &&
                  <TabContainer iconButton="drive_eta" label={messages['infoBubble_landmarkInfo']} index={0} style={{ padding: 0 }}>
                    <LandmarkInfo data={displayData} messages={messages} loading={loading} />
                  </TabContainer>
                }

                {
                  type === "Geofence" && !isEtaLink &&
                  <TabContainer iconButton="drive_eta" label={messages['infoBubble_geofenceInfo']} index={0} style={{ padding: 0 }}>
                    <GeofenceInfo data={displayData} messages={messages} loading={loading} />
                  </TabContainer>
                }

                {
                  type === "RoutePoint" && !isEtaLink &&
                  <TabContainer iconButton="drive_eta" label={messages['infoBubble_routePointInfo']} index={0} style={{ padding: 0 }}>
                    <RoutePointInfo data={response} messages={messages} />
                  </TabContainer>
                }

                {
                  type === "RoutePoint" && isEtaLink &&
                  <TabContainer iconButton="drive_eta" label={messages['infoBubble_routePointInfo'] || 'Info'} index={0} style={{ padding: 0 }}>
                    <EtaLinkRoutePointInfo mapObject={props} messages={messages} />
                  </TabContainer>
                }

                {
                  type === "RoutePoint" && !isEtaLink &&
                  <TabContainer iconButton="history" label={messages['infoBubble_routePointInfo_history']} index={1} style={{ padding: 0 }}>
                    <RoutePointInfoLog data={response} messages={messages} />
                  </TabContainer>
                }

                {
                  (type === "Vehicle" || type === "Trail") && !isEtaLink &&
                  <TabContainer iconButton="room" label={messages['infoBubble_location']} index={0} style={{ padding: 0 }}>
                    <Location id={id} type={type} user={user} data={displayDataWithRealtimeUnit} messages={messages} locale={locale} mapRef={mapReference} loading={loading} />
                  </TabContainer>
                }

                {
                  (type === "Vehicle") && isEtaLink &&
                  <TabContainer iconButton="room" label={messages['infoBubble_location'] || 'Location'} index={0} style={{ padding: 0 }}>
                    <EtaLinkVehicleContent
                      data={displayDataWithRealtimeUnit}
                      messages={messages}
                      position={position}
                      id={id}
                      type={type}
                      user={user}
                      response={effectiveResponse}
                      isEtaLink={isEtaLink}
                      hasSnapshot={hasVideo() && (scheduleSnapshot || effectiveResponse?.markerDetail?.unit?.manufacturerName?.toUpperCase() === LIGTHMETRICS_MANUFACTURER)}
                    />
                  </TabContainer>
                }

                {
                  (type === "Vehicle" || type === "Trail") && !isEtaLink &&
                  <TabContainer iconButton="drive_eta" label={messages['infoBubble_vehicleInfo']} index={1} style={{ padding: 0 }}>
                    <VehicleInfo id={id} type={type} user={user} data={displayDataWithRealtimeUnit} messages={messages} loading={loading} />
                  </TabContainer>
                }

                {
                  (type === "Vehicle" || type === "Trail") && !isEtaLink &&
                  <TabContainer iconButton="settings_remote" label={messages['infoBubble_deviceInfo']} index={2} style={{ padding: 0 }}>
                    <DeviceInfo id={id} type={type} user={user} data={displayDataWithRealtimeUnit} messages={messages} loading={loading} trailUnitId={deviceId} />
                  </TabContainer>
                }

                {
                  !isEtaLink &&
                  <TabContainer
                    iconButton="streetview"
                    label={messages['infoBubble_streetView']}
                    index={type === "Landmark" || type === "Geofence" ? 1 : type === "RoutePoint" ? 2 : 3}
                    style={{ padding: 0 }}
                  >
                    <div className='info-window-tab-streetview'>
                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        style={{
                          padding: 20
                        }}
                      >
                        <Grid item xs>
                          <h2 style={{ margin: 0 }}>
                            {messages['infoBubble_streetView']}
                          </h2>
                        </Grid>
                      </Grid>

                      <div style={{ borderTop: 'solid 1px #e4e4e4' }}></div>

                      {
                        errors?.streetView ?
                          <div style={{ fontSize: 16, fontWeight: 'bold', padding: 16 }}>
                            {errors.streetView}
                          </div>
                          :
                          <div
                            ref={el => { streeViewContainer.current = el; setStreetViewVisible(!!el); }}
                            className='info-window-size-width'
                            style={{ height: 295 }}
                          >
                          </div>
                      }
                    </div>
                  </TabContainer>
                }
                {
                  hasVideo() && !isEtaLink && HasPermission(user, 'vidfleetvideo', [217]) &&
                  <TabContainer
                    iconButton="videocam"
                    label={messages['infoBubble_videocam']}
                    index={4}
                    style={{ padding: response?.markerDetail?.linkedDevice?.manufacturerName === "VidFleet" ? '10px 0px 0px' : 0 }}
                    destroyAfterChange={true}
                  >
                    <div className='info-window-size-width' style={{ minHeight: 290 }}>
                      {getVideo}
                    </div>
                  </TabContainer>
                }

                {
                  type === "Trail" && !isEtaLink &&
                  <TabContainer
                    iconButton="settings"
                    label={messages['infoBubble_settings']}
                    index={(hasVideo() ? 5 : 4)}
                    style={{ padding: 0 }}
                  >
                    <Settings id={id} user={user} authenticatedId={authenticatedId} data={response} messages={messages} />
                  </TabContainer>
                }

                {/* {
                  ((internalVideoTab === 2 || (internalVideoTab === 1 && type === "Trail")) && mainTab === 3 && response?.markerDetail?.linkedDevice?.manufacturerName === "VidFleet") &&
                  <TabButton
                    tooltip={tooltip}
                    disabled={loadingCreateClip || !availableClips.length || disableBtn}
                    className="btn-blue"
                    onClick={() => {
                      dispatch(clipRequestOpenForm(true))
                      dispatch(clipRequestGetUnits());
                      closeInfoBubble()
                    }}
                    title={tabButton?.title} 
                  />
                } */}
                {
                  type === "Vehicle" && !isEtaLink &&
                  <TabIconButton
                    onClick={(e) => {
                      const unitInfo = units?.find(item => item?.id == response?.id)
                      e.preventDefault();
                      dispatch(
                        openOverViewUnit({
                          data: { unit: unitInfo, reading: lastReading },
                          open: true,
                        })
                      );
                    }}
                    icon="gps_fixed"
                    size="small"
                    tooltip={messages["unitOverViewPanel"]}
                  />
                }
                {
                  type === "Vehicle" && mainTab !== 2
                  &&
                  HasPermission(user, "trbusr", [262]) && !isEtaLink &&
                  <TabIconButton
                    onClick={(e) => {
                      e.preventDefault();
                      dispatch(openTroubleshootDeviceModal({ open: true, unit: response.markerDetail.unit }))
                    }}
                    icon="troubleshoot"
                    size="small"
                    tooltip={messages["troubleshooting"]}
                  />
                }
                {
                  showOemAction &&
                  <TabIconButton
                    onClick={(e) => {
                      e.preventDefault();
                      oemAction.open();
                    }}
                    icon={OEM_DATA_ICON}
                    size="small"
                    disabled={oemAction.disabled}
                    tooltip={oemAction.tooltip}
                  />
                }
                {!isEtaLink && getMaintenanceWarnings()}
                {
                  (mainTab === 4 && response?.markerDetail?.linkedDevice?.manufacturerName === "SmartWitness") && !isEtaLink &&
                  <TabButton
                    disabled={!isSmartWitnessVideoAvailable}
                    className="btn-blue"
                    onClick={(e) => {
                      e.preventDefault();
                      smartwitnessRef?.current?.save();
                    }}
                    title={messages['save']}
                  />
                }

                {
                  type === "Vehicle"
                  &&
                  mainTab === 0
                  &&
                  HasPermission(user, '', [32])
                  && !isEtaLink &&
                  <TabButton
                    className="btn-blue modalTrailButtonStyle"
                    onClick={() => {
                      const { isKph } = user
                      let startDate = new Date()
                      startDate.setHours(0, 0, 0);
                      let endDate = new Date()
                      endDate.setHours(23, 59, 0);

                      // Switching markers clears getMarkerDetailsResponse while
                      // the new request is in-flight. The bubble still shows
                      // preview data, so Get Trail must not read `response`.
                      const unitId = id?.toString()?.split('_')?.[1] || id;
                      const catalogUnit = units?.find((item) => String(item.id) === String(unitId));
                      const markerUnit = displayDataWithRealtimeUnit?.markerDetail?.unit;
                      const clonedMarkerUnit = markerUnit != null ? structuredClone(markerUnit) : markerUnit;
                      const clonedCatalogUnit = catalogUnit != null ? structuredClone(catalogUnit) : catalogUnit;
                      const trailUnit = clonedCatalogUnit
                        ? { ...clonedMarkerUnit, ...clonedCatalogUnit, id: clonedCatalogUnit.id }
                        : clonedMarkerUnit;

                      const paramToGetTrails = {
                        unitId: trailUnit?.id || unitId,
                        event: '0',
                        startDate: moment.utc(startDate).format(),
                        endDate: moment.utc(endDate).format(),
                        limit: preferences?.trailRowsPerPage || 100,
                        offset: 0,
                        useEcuSpeed: trailUnit?.useEcuSpeed,
                        unitType: trailUnit?.unitType || trailUnit?.lastReading?.unitType,
                        kph: isKph
                      }

                      dispatch(selectModuleMapSettings(MapUtilitiesTypes.VehicleTrails));
                      // Mark the active trail unit so mapsController can
                      // detect `hasVideo` and render a clickable polyline
                      // that opens the Clip Request drawer. Without this
                      // dispatch `state.vehicleTrailsSliceRedux.unitVehicleTrails`
                      // stays null and the trail line is rendered as
                      // non-clickable (parity with VehicleTrailsForm and
                      // VehicleTripsMode, which both dispatch this action).
                      dispatch(setUnitVehicleTrail(trailUnit));
                      dispatch(getVehicleTrails(paramToGetTrails));
                      dispatch(setVehicleTrailToPrint(trailUnit));
                      if (trailUnit?.typeId) {
                        dispatch(searchUnitTypeEvents(trailUnit.typeId));
                      }
                    }}
                    title={messages['realtimemaps_vehicletrails_gettrail_infoBubble']}
                  >
                  </TabButton>
                }
                {
                  type === "Vehicle" && mainTab === 2 && HasPermission(user, "trbusr", [262]) && !isEtaLink &&
                  <TabButton
                    className="btn-blue modalTrailButtonStyle"
                    onClick={(e) => {
                      e?.preventDefault();
                      dispatch(openTroubleshootDeviceModal({ open: true, unit: response.markerDetail.unit }))
                    }}
                    title={messages["troubleshooting"]}
                  >
                  </TabButton>
                }
              </Tabs>
            }
            {
              oemAction.modal &&
              // Clicks in the portal'd modal bubble through the React tree; keep them out of the bubble.
              <span onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                {oemAction.modal}
              </span>
            }

            {
              (errors && errors.markerDetails) &&
              <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
                style={{ padding: 20, height: 300 }}
              >
                <Grid item>
                  <span style={{ fontSize: 14 }}>{errors.markerDetails}</span>
                </Grid>

                <Grid item>
                  <Button
                    type="icon"
                    icon="refresh"
                    onClick={() => {
                      if (isEtaLink) {
                        dispatch(getPublicMarkerDetails(deviceId));
                      } else {
                        dispatch(getMarkerDetails({ type, id, unitId: deviceId }));
                      }
                    }}
                  />
                </Grid>
              </Grid>
            }
          </div>
      }
    </div>
  );
}

export default memo(InfoBubble);
